"use client"

import React from "react"

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount || 0)

const formatNumber = (n, digits = 2) =>
  Number(n || 0).toFixed(digits)

const ElectricalSectionSummaryBox = ({ sectionsCalc }) => {
  const sectionsArray = sectionsCalc
    ? Object.values(sectionsCalc)
    : []

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:col-span-3">
      <h2 className="mb-3 text-md font-semibold text-slate-900">
        Section Overview
      </h2>

      <div className="mb-3 text-md font-semibold text-slate-900 bg-yellow-100 p-2 rounded-lg w-1/4">
        Project Total Cost: {" "}
        {formatCurrency(
          sectionsArray.reduce(
            (acc, section) => {
              const subTotal = section.sub_total || 0
              const markupValue = section.markup_value || 0
              const profitValue = section.profit_value || 0
              return acc + subTotal + markupValue + profitValue
            },
            0
          )
        )}
      <br />
      <hr />
        Project Total Hrs: {" "}
        {formatNumber(
          sectionsArray.reduce(
            (acc, section) => {
              const subTotalLaborHrs = section.sub_total_labor_hrs || 0
              return acc + subTotalLaborHrs
            },
            0
          ), 2
        )}
      </div>

      {sectionsArray.length === 0 ? (
        <p className="text-sm text-slate-500">
          Once you add items and save, you&apos;ll see per-section cost breakdown here.
        </p>
      ) : (
        <div className="space-y-4">
          {sectionsArray.map((section) => {
            const sid = section.section_id
            const rows = section.rows || []
            const subTotal = section.sub_total || 0
            const markupRate = section.markup_rate || 0
            const markupValue = section.markup_value || 0
            const profitRate = section.profit_rate || 0
            const profitValue = section.profit_value || 0
            const subTotalLaborHrs = section.sub_total_labor_hrs || 0
            const sectionUnits = section.section_units || 0
            const subSectionTotal = subTotal + markupValue + profitValue

            return (
              <div
                key={sid}
                className="rounded-lg border border-slate-200 bg-slate-50 p-3"
              >
                {/* Section header */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-md font-semibold text-slate-900">
                      {section.type}
                      {sid != null && (
                        <span className="ml-1 text-sm text-slate-500">
                          (ID #{sid})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-slate-600">
                    <span className="mr-3">
                      Section Units:{" "}
                      <span className="font-semibold text-slate-900">
                        {formatNumber(sectionUnits, 2)}
                      </span>
                    </span>
                    {/* You can also add line count if you want, but PHP can give it too */}
                  </div>
                </div>

                {/* Table */}
                <div className="mt-3 overflow-x-auto rounded-md border border-slate-200 bg-white">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-2 py-1 text-left font-medium text-slate-600">
                          Description
                        </th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">
                          Detail Unit
                        </th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">
                          Unit Cost
                        </th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">
                          Summary Cost
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={4}
                            className="px-2 py-2 text-center text-slate-400"
                          >
                            No rows calculated for this section.
                          </td>
                        </tr>
                      ) : (
                        rows.map((r) => (
                          <tr key={r.label} className="border-t border-slate-100">
                            <td className="px-2 py-1 text-slate-800">
                              {r.label}
                            </td>
                            <td className="px-2 py-1 text-right text-slate-800">
                              {formatNumber(r.detail_unit, 2)}
                            </td>
                            <td className="px-2 py-1 text-right text-slate-700">
                              {formatCurrency(r.unit_cost)}
                            </td>
                            <td className="px-2 py-1 text-right font-semibold text-slate-900">
                              {formatCurrency(r.extended)}
                            </td>
                          </tr>
                        ))
                      )}

                      {/* Sub Total */}
                      <tr className="border-t border-slate-200 bg-slate-50">
                        <td className="px-2 py-1 text-right text-slate-700" colSpan={3}>
                          Sub Total
                        </td>
                        <td className="px-2 py-1 text-right font-semibold text-slate-900">
                          {formatCurrency(subTotal)}
                        </td>
                      </tr>

                      {/* Markup */}
                      <tr className="border-t border-slate-100">
                        <td className="px-2 py-1 text-right text-slate-700" colSpan={3}>
                          Markup ({formatNumber(markupRate, 2)}%)
                        </td>
                        <td className="px-2 py-1 text-right font-semibold text-slate-900">
                          {formatCurrency(markupValue)}
                        </td>
                      </tr>

                      {/* Profit */}
                      <tr className="border-t border-slate-100">
                        <td className="px-2 py-1 text-right text-slate-700" colSpan={3}>
                          Profit ({formatNumber(profitRate, 2)}%)
                        </td>
                        <td className="px-2 py-1 text-right font-semibold text-slate-900">
                          {formatCurrency(profitValue)}
                        </td>
                      </tr>

                      {/* Sub Total Labor Hrs */}
                      <tr className="border-t border-slate-100">
                        <td className="px-2 py-1 text-right text-slate-700" colSpan={3}>
                          Sub Total Labor Hrs
                        </td>
                        <td className="px-2 py-1 text-right font-semibold text-slate-900">
                          {formatNumber(subTotalLaborHrs, 2)}
                        </td>
                      </tr>

                      {/* Sub Section Total */}
                      <tr className="border-t border-slate-100">
                        <td className="px-2 py-1 text-right text-indigo-700"  colSpan={3}>
                          Sub Section Total
                        </td>
                        <td className="px-2 py-1 text-right font-bolder text-indigo-700">
                          {formatCurrency(subSectionTotal, 2)}
                        </td>
                      </tr>                      
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ElectricalSectionSummaryBox
